import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import DocumentStatusBadge from './DocumentStatusBadge';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useLanguage } from '../../../contexts/LanguageContext';

const DocumentTableRow = ({
  document,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onRestore,
  onDownload,
  filterState
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const getCategoryIcon = (category) => {
    const icons = {
      rams: 'FileText',
      insurance: 'Shield',
      training: 'GraduationCap',
      plant: 'Wrench'
    };
    return icons?.[category] || 'File';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No expiry';
    const date = new Date(dateString);
    if (isNaN(date?.getTime())) return 'No expiry';
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getComputedStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'no-expiry', daysUntilExpiry: null };
    const date = new Date(expiryDate);
    if (isNaN(date?.getTime())) return { status: 'no-expiry', daysUntilExpiry: null };
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    const diffMs = date - today;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { status: 'expired', daysUntilExpiry: diffDays };
    if (diffDays <= 30) return { status: 'expiring-soon', daysUntilExpiry: diffDays };
    return { status: 'valid', daysUntilExpiry: diffDays };
  };

  const expiryDate = document?.expiry_date || document?.expiryDate;
  const { status, daysUntilExpiry } = getComputedStatus(expiryDate);

  const handleRowClick = () => {
    navigate(`/documents/${document?.id}`, { state: { document, filterState } });
  };

  const stopPropagation = (e) => e?.stopPropagation();

  return (
    <tr
      className="border-b border-border hover:bg-muted/50 transition-smooth cursor-pointer"
      onClick={handleRowClick}
    >
      <td className="px-4 py-4" onClick={stopPropagation}>
        <Checkbox
          checked={isSelected}
          onChange={(e) => onSelect(document?.id, e?.target?.checked)}
        />
      </td>
      <td className="px-4 py-4">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg flex-shrink-0">
            <Icon name={getCategoryIcon(document?.category)} size={20} color="var(--color-accent)" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-foreground truncate hover:text-accent transition-colors">{document?.title}</p>
            <p className="text-sm text-muted-foreground truncate">{document?.siteName || document?.sites?.site_name}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-4">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-muted rounded-md text-sm text-foreground">
          {document?.categoryLabel || document?.category}
        </span>
      </td>
      <td className="px-4 py-4">
        <p className="text-sm text-foreground">{document?.owner}</p>
      </td>
      <td className="px-4 py-4">
        <p className="text-sm text-foreground whitespace-nowrap">{formatDate(document?.issueDate || document?.issue_date)}</p>
      </td>
      <td className="px-4 py-4">
        <p className="text-sm text-foreground whitespace-nowrap">{formatDate(expiryDate)}</p>
      </td>
      <td className="px-4 py-4">
        <DocumentStatusBadge status={status} daysUntilExpiry={daysUntilExpiry} />
      </td>
      <td className="px-4 py-4" onClick={stopPropagation}>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDownload(document)}
            aria-label={t('downloadDocument')}
            title={t('download')}
          >
            <Icon name="Download" size={18} />
          </Button>
          {onEdit && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(document)}
              aria-label={t('editDocument')}
              title={t('edit')}
            >
              <Icon name="Edit" size={18} />
            </Button>
          )}
          {onDelete && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(document)}
              aria-label={t('deleteDocument')}
              title={t('delete')}
            >
              <Icon name="Trash2" size={18} className="text-destructive" />
            </Button>
          )}
          {onRestore && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRestore(document)}
              aria-label="Restore document"
              title="Restore"
            >
              <Icon name="RotateCcw" size={18} className="text-accent" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default DocumentTableRow;